import type { CSSProperties } from 'react';

/**
 * Dot color for a channel's environment, used by the "Channels Using It" pills
 * on the Data Privacy and Live Agent Platforms pages. Live reads as "ready"
 * (green), sandbox as "in progress" (amber) — matching the `live ? 'success' :
 * 'default'` env convention used elsewhere.
 */
export const getEnvDotColor = (env?: string): string => {
  const normalized = (env ?? '').toLowerCase();
  if (normalized === 'live') return '#2e9e5b';
  if (normalized === 'sandbox') return '#f5a623';
  return '#b0b4bb';
};

/**
 * Inline style for the small env dot — the Sandbox/Live segmented control
 * toggle (as a `<span slot="prefix" />`) and the "Channels Using It" pills,
 * which name ONE environment and therefore always pass `active: true`. Shared
 * so the two stay visually consistent: written out by hand, six properties had
 * to be kept in step by eye.
 *
 * The active item shows its env color; inactive items show the neutral grey.
 */
export const getEnvDotStyle = (
  // Optional for the same reason `getEnvDotColor` is: a channel row carries
  // `env?: string`, and an absent env is a real state that resolves to the
  // neutral grey rather than a call-site `?? ''`.
  env: string | undefined,
  active: boolean
): CSSProperties => ({
  display: 'inline-block',
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  flex: '0 0 auto',
  background: active ? getEnvDotColor(env) : getEnvDotColor(),
});
